import { useState, useEffect } from "react";
import { api, describeAxiosError } from "@/lib/api";
import { X, Loader2, Upload, Image as ImageIcon } from "lucide-react";

const TABS = ["Personal", "Contact", "Employment", "Bank", "Statutory"];

export default function EditEmployeeModal({ employee, departments = [], onClose, onSaved }) {
  const [form, setForm] = useState({ ...employee });
  const [tab, setTab] = useState("Personal");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    api.get("/employees").then((r) => setManagers(r.data.filter((e) => e.id !== employee.id)));
  }, [employee.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k, v) => setForm((f) => ({ ...f, [k]: v === "" ? null : Number(v) }));

  // Split a Monthly Gross into the standard 5 components (Basic 50%, HRA 20%,
  // Conveyance ₹800, Medical ₹1,250, Special = remainder). Called when the
  // admin edits the Monthly Gross field so downstream components stay in sync.
  const applyGross = (grossStr) => {
    if (grossStr === "" || grossStr == null) {
      setForm((f) => ({ ...f, gross_input: "" }));
      return;
    }
    const gross = Number(grossStr) || 0;
    const basic = Math.round(gross * 0.5);
    const hra = Math.round(gross * 0.2);
    const conveyance = gross > 0 ? 800 : 0;
    const medical = gross > 0 ? 1250 : 0;
    const special_allowance = Math.max(0, gross - basic - hra - conveyance - medical);
    setForm((f) => ({
      ...f,
      gross_input: grossStr,
      basic, hra, conveyance, medical, special_allowance,
      ctc_annual: gross * 12,
    }));
  };

  // Compute a helpful "current gross" from stored components for the placeholder
  const currentGross = (Number(form.basic) || 0) + (Number(form.hra) || 0)
    + (Number(form.conveyance) || 0) + (Number(form.medical) || 0)
    + (Number(form.special_allowance) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const dept = departments.find((d) => d.id === form.department_id);
      const { gross_input, ...clean } = form;  // strip UI-only helper field
      const payload = { ...clean, department_name: dept?.name || form.department_name };
      const { data } = await api.put(`/employees/${employee.id}`, payload);
      onSaved(data);
    } catch (err) {
      console.error("Employee update failed:", err.response?.status, err.response?.data, err);
      setError(describeAxiosError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="edit-employee-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>{employee.employee_code}</div>
            <h2 className="font-heading text-xl font-bold">Edit · {employee.full_name}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-1 border-b border-stone-200 mb-5 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} type="button" onClick={() => setTab(t)} data-testid={`edit-tab-${t.toLowerCase()}`}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t ? "border-primary text-primary" : "border-transparent text-stone-500 hover:text-stone-900"}`}>
              {t}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {tab === "Personal" && (
            <div className="grid grid-cols-2 gap-3">
              <In label="First Name" v={form.first_name} on={(v) => set("first_name", v)} required />
              <In label="Last Name" v={form.last_name} on={(v) => set("last_name", v)} required />
              <div className="col-span-2">
                <PhotoUpload employee={employee} photoUrl={form.photo_url}
                             onUploaded={(url) => set("photo_url", url)} />
              </div>
              <Sel label="Gender" v={form.gender} on={(v) => set("gender", v)}>
                <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
              </Sel>
              <In label="Date of Birth" type="date" v={form.dob} on={(v) => set("dob", v)} />
              <Sel label="Blood Group" v={form.blood_group} on={(v) => set("blood_group", v)}>
                <option value="">—</option>{["A+","A-","B+","B-","O+","O-","AB+","AB-"].map((g)=> <option key={g}>{g}</option>)}
              </Sel>
              <Sel label="Marital Status" v={form.marital_status} on={(v) => set("marital_status", v)}>
                <option value="">—</option><option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
              </Sel>
              <In label="Aadhaar" v={form.aadhaar} on={(v) => set("aadhaar", v)} />
              <In label="PAN" v={form.pan} on={(v) => set("pan", v)} />
              <In label="Passport" v={form.passport} on={(v) => set("passport", v)} />
              <In label="Driving License" v={form.driving_license} on={(v) => set("driving_license", v)} />
            </div>
          )}
          {tab === "Contact" && (
            <div className="grid grid-cols-2 gap-3">
              <In label="Mobile" v={form.mobile} on={(v) => set("mobile", v)} />
              <In label="Alternate Mobile" v={form.alternate_mobile} on={(v) => set("alternate_mobile", v)} />
              <In label="Email" type="email" v={form.email} on={(v) => set("email", v)} required />
              <In label="Current Address" v={form.current_address} on={(v) => set("current_address", v)} />
              <In label="Permanent Address" v={form.permanent_address} on={(v) => set("permanent_address", v)} />
            </div>
          )}
          {tab === "Employment" && (
            <div className="grid grid-cols-2 gap-3">
              <In label="Joining Date" type="date" v={form.joining_date} on={(v) => set("joining_date", v)} />
              <In label="Confirmation Date" type="date" v={form.confirmation_date} on={(v) => set("confirmation_date", v)} />
              <Sel label="Department" v={form.department_id} on={(v) => set("department_id", v)}>
                <option value="">—</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Sel>
              <In label="Designation" v={form.designation} on={(v) => set("designation", v)} />
              <Sel label="Company" v={form.company || "NAVARATNAA"} on={(v) => set("company", v)} data-testid="employee-company-select">
                <option value="NAVARATNAA">Navaratnaa</option>
                <option value="SHERATAAN">Sherataan</option>
              </Sel>
              <Sel label="Reporting Manager" v={form.reporting_manager_id || ""} on={(v) => {
                const m = managers.find((x) => x.id === v);
                setForm((f) => ({ ...f, reporting_manager_id: v || null, reporting_manager: m?.full_name || null }));
              }} data-testid="reporting-manager-select">
                <option value="">— None —</option>
                {managers.map((m) => <option key={m.id} value={m.id}>{m.full_name} · {m.designation}</option>)}
              </Sel>
              <In label="Grade" v={form.grade} on={(v) => set("grade", v)} />
              <Sel label="Employment Type" v={form.employment_type} on={(v) => set("employment_type", v)}>
                {["Permanent","Probation","Contract","Intern","Consultant"].map((t)=> <option key={t}>{t}</option>)}
              </Sel>
              <In label="Location" v={form.location} on={(v) => set("location", v)} placeholder="e.g. HYDERABAD" />
              <label className="block col-span-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                <div className="kpi-label mb-1.5 text-amber-900">Monthly Gross (₹) — auto-splits components below</div>
                <input type="number" value={form.gross_input ?? currentGross ?? ""}
                       onChange={(e) => applyGross(e.target.value)}
                       placeholder={currentGross ? `Current: ₹${currentGross.toLocaleString("en-IN")}` : "e.g. 22000"}
                       data-testid="edit-gross-input"
                       className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-white focus:outline-none focus:border-amber-600" />
                <div className="text-xs text-amber-800 mt-1">
                  Basic 50% · HRA 20% · Conveyance ₹800 · Medical ₹1,250 · Special = remainder.
                  Editing this recomputes all 5 fields.
                </div>
              </label>
              <In label="Basic (₹/month)" type="number" v={form.basic} on={(v) => setNum("basic", v)} />
              <In label="HRA (₹/month)" type="number" v={form.hra} on={(v) => setNum("hra", v)} />
              <In label="Conveyance (₹/month)" type="number" v={form.conveyance} on={(v) => setNum("conveyance", v)} />
              <In label="Medical (₹/month)" type="number" v={form.medical} on={(v) => setNum("medical", v)} />
              <In label="Special Allowance (₹/month)" type="number" v={form.special_allowance} on={(v) => setNum("special_allowance", v)} />
              <In label="CTC Annual (₹)" type="number" v={form.ctc_annual} on={(v) => setNum("ctc_annual", v)} />
              <Sel label="Status" v={form.status} on={(v) => set("status", v)}>
                {["active","inactive","exited"].map((s)=> <option key={s}>{s}</option>)}
              </Sel>
            </div>
          )}
          {tab === "Bank" && (
            <div className="grid grid-cols-2 gap-3">
              <In label="Bank Name" v={form.bank_name} on={(v) => set("bank_name", v)} />
              <In label="Account Number" v={form.account_number} on={(v) => set("account_number", v)} />
              <In label="IFSC" v={form.ifsc} on={(v) => set("ifsc", v)} />
              <In label="Branch" v={form.branch} on={(v) => set("branch", v)} />
            </div>
          )}
          {tab === "Statutory" && (
            <div className="grid grid-cols-2 gap-3">
              <In label="PF Number" v={form.pf_number} on={(v) => set("pf_number", v)} />
              <In label="UAN" v={form.uan} on={(v) => set("uan", v)} />
              <In label="ESI Number" v={form.esi_number} on={(v) => set("esi_number", v)} />
              <In label="Professional Tax" v={form.professional_tax} on={(v) => set("professional_tax", v)} placeholder="₹200/month" />
              <label className="flex items-center gap-2 col-span-2 p-2 rounded-md border border-stone-200 bg-stone-50/50">
                <input type="checkbox" checked={form.epf_applicable !== false}
                       onChange={(e) => set("epf_applicable", e.target.checked)}
                       data-testid="epf-applicable-toggle" />
                <span className="text-sm">EPF Applicable (12% employee + 12% employer on Basic, capped at ₹15,000)</span>
              </label>
              <label className="flex items-center gap-2 col-span-2 p-2 rounded-md border border-stone-200 bg-stone-50/50">
                <input type="checkbox" checked={form.esi_applicable !== false}
                       onChange={(e) => set("esi_applicable", e.target.checked)}
                       data-testid="esi-applicable-toggle" />
                <span className="text-sm">ESI Applicable (auto-skipped when gross ≥ ₹21,000)</span>
              </label>
              <label className="flex items-center gap-2 col-span-2 p-2 rounded-md border border-stone-200 bg-stone-50/50">
                <input type="checkbox" checked={form.pt_applicable !== false}
                       onChange={(e) => set("pt_applicable", e.target.checked)}
                       data-testid="pt-applicable-toggle" />
                <span className="text-sm">PT Applicable (Telangana slab: ₹200 if gross &gt; ₹20k, ₹150 if ₹15k–20k, else ₹0)</span>
              </label>
            </div>
          )}
          {error && <div className="text-sm px-3 py-2 rounded-md" style={{ background: "#FADED7", color: "#9A3412" }}>{error}</div>}
          <div className="flex gap-3 pt-3 border-t border-stone-100">
            <button type="button" onClick={onClose}
                    className="flex-1 py-2 rounded-md border border-stone-300 text-sm font-medium hover:bg-stone-50">Cancel</button>
            <button type="submit" disabled={busy} data-testid="save-employee-button"
                    className="flex-1 py-2 rounded-md text-white text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ background: "var(--color-primary)" }}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function In({ label, type = "text", v, on, required, placeholder }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} required={required} placeholder={placeholder}
             className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
    </label>
  );
}
function Sel({ label, v, on, children, ...rest }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <select value={v ?? ""} onChange={(e) => on(e.target.value)} {...rest}
              className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white focus:outline-none focus:border-primary">{children}</select>
    </label>
  );
}

function PhotoUpload({ employee, photoUrl, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(photoUrl);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErr("Photo too large (max 5 MB)");
      return;
    }
    // Instant preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/employees/${employee.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.photo_url);
      setPreview(data.photo_url);
    } catch (e2) {
      setErr(describeAxiosError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="kpi-label mb-1.5">Profile Photo</div>
      <div className="flex items-center gap-4 p-3 rounded-md border border-stone-200 bg-stone-50/40">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-stone-200 flex items-center justify-center flex-shrink-0 border-2 border-white shadow-sm">
          {preview ? (
            <img src={preview} alt="Profile" className="w-full h-full object-cover"
                 onError={(e) => { e.target.style.display = "none"; }} data-testid="photo-preview" />
          ) : (
            <ImageIcon className="w-8 h-8 text-stone-400" />
          )}
        </div>
        <div className="flex-1">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-stone-300 text-sm font-medium cursor-pointer hover:bg-white"
                 data-testid="photo-upload-label">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Uploading…" : (preview ? "Replace Photo" : "Upload Photo")}
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={onPick}
                   disabled={busy} className="hidden" data-testid="photo-upload-input" />
          </label>
          <div className="text-xs text-stone-500 mt-1.5">JPG, PNG, GIF or WebP · max 5 MB · square photos look best.</div>
          {err && <div className="text-xs mt-1.5" style={{ color: "#9A3412" }} data-testid="photo-upload-error">{err}</div>}
        </div>
      </div>
    </div>
  );
}
